import React, { useState, useEffect } from 'react';

interface ProfileCompletionPopupProps {
    isVisible: boolean;
    completionPercentage: number;
    onClose: () => void;
    onCompleteProfile: () => void;
}

export const ProfileCompletionPopup: React.FC<ProfileCompletionPopupProps> = ({
    isVisible,
    completionPercentage,
    onClose,
    onCompleteProfile
}) => {
    const [isAnimatingOut, setIsAnimatingOut] = useState(false);

    useEffect(() => {
        if (isVisible) {
            // Auto-hide after 5 seconds
            const timer = setTimeout(() => {
                handleClose();
            }, 5000);

            return () => clearTimeout(timer);
        }
    }, [isVisible]);

    const handleClose = () => {
        setIsAnimatingOut(true);
        setTimeout(() => {
            onClose();
            setIsAnimatingOut(false);
        }, 300);
    };

    // Don't show popup if profile is 100% complete
    if (completionPercentage >= 100) return null;
    
    if (!isVisible && !isAnimatingOut) return null;

    return (
        <div className={`fixed bottom-4 left-4 z-50 transition-all duration-300 ${
            isAnimatingOut ? 'opacity-0 transform translate-y-2' : 'opacity-100 transform translate-y-0'
        }`}>
            <div className="bg-white rounded-lg shadow-lg border border-primary-200 p-4 max-w-sm">
                <div className="flex items-start gap-3">
                    {/* Progress circle */}
                    <div className="relative w-12 h-12 flex-shrink-0">
                        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                            {/* Background circle */}
                            <circle
                                cx="50"
                                cy="50"
                                r="40"
                                stroke="#E5E7EB"
                                strokeWidth="8"
                                fill="none"
                            />
                            {/* Progress circle */}
                            <circle
                                cx="50"
                                cy="50"
                                r="40"
                                stroke="#F59E0B"
                                strokeWidth="8"
                                fill="none"
                                strokeDasharray={`${completionPercentage * 2.51} 251`}
                                strokeLinecap="round"
                                className="transition-all duration-1000"
                            />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-xs font-bold text-orange-600">
                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                            </span>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                        <h3 className="text-grayscale-1300 font-grotesk text-sm font-bold mb-1">
                            Complete Your Profile
                        </h3>
                        <p className="text-grayscale-1000 font-grotesk text-xs mb-3">
                            Add your goals and challenges to get sharper matches — and to unlock university contact features.
                        </p>
                        <div className="flex gap-2">
                            <button
                                onClick={onCompleteProfile}
                                className="px-3 py-1.5 bg-primary-og-600 text-white font-grotesk text-xs font-medium rounded-full hover:bg-primary-700 transition-colors"
                            >
                                Complete Now
                            </button>
                            <button
                                onClick={handleClose}
                                className="px-3 py-1.5 border border-gray-300 text-grayscale-1200 font-grotesk text-xs font-medium rounded-full hover:bg-gray-50 transition-colors"
                            >
                                Later
                            </button>
                        </div>
                    </div>

                    {/* Close button */}
                    <button
                        onClick={handleClose}
                        className="flex-shrink-0 p-1 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    );
};
